/**
 * "💬 Nudge for feedback" — DM whoever owes a scorecard.
 *
 * Its own module because useBoardActions.ts sits on the repo's 500-line ceiling, and because this
 * action now has a shape worth stating on its own: **recipients come from the PROXY** (Lever's
 * panels), never from this client, while the WORDS come from the recruiter — their edit of the
 * draft the proxy built, which the proxy escapes and caps before sending.
 *
 * The toast names which identity actually sent it. The modal promised one of the two before the
 * click, and a promise of "from your Slack" that quietly fell back to the bot is exactly the
 * thing a recruiter should hear about rather than discover later in a thread.
 */
import { showToast } from '@finn-auto/design-system/atoms/toast';
import * as api from '../../services/taActionBoardApi';
import type { NudgeSentAs } from '../../services/taActionBoardApi';
import type { RoleBoard, SlaActNowItem } from '../../types/taActionBoard';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import { reportFeedbackNudge } from './boardAnalytics';

/** @param text the recruiter's edit of the draft; omitted means send the proxy's default. */
/**
 * Which identity actually sent, in words.
 *
 * The modal promised one of the two before the click, so a promise of "from your Slack" that
 * quietly became the bot is worth surfacing. `mixed` is a real outcome — the personal token is
 * resolved per DM, so one lapsing mid-nudge splits a single send across both identities — and
 * claiming either one would be a claim about messages it did not cover.
 */
function sentBy(sentAs: NudgeSentAs): string {
  if (sentAs === 'me') return ' from your Slack';
  if (sentAs === 'mixed') return ' — some from your Slack, some by the TA bot';
  return ' by the TA bot';
}

const plural = (n: number) => `${n} interviewer${n === 1 ? '' : 's'}`;

export const nudgeAction = (
  role: RoleBoard,
  item: SlaActNowItem,
  text?: string,
  /** Only used when `text` is empty — the proxy builds its fallback in it. */
  language?: 'de' | 'en'
) => {
      api
        .sendFeedbackNudge({
          opportunityId: item.opportunityId,
          roleTitle: role.roleTitle,
          text,
          ...(language ? { language } : {}),
        })
        .then((r) => {
          reportFeedbackNudge(r.sent.length, !r.ok);
          return r;
        })
        .then((r) =>
          showToast({
            status: r.ok ? 'success' : 'warning',
            description: r.ok
              ? `Reminder sent to ${plural(r.sent.length)}${sentBy(r.sentAs)}.`
              : r.sent.length > 0
                ? // A partial send still reached someone, and by whom is the same question it
                  // is on a clean one — naming only the failures leaves that unanswered.
                  `Reminder sent to ${plural(r.sent.length)}${sentBy(r.sentAs)}; ${r.failed.length} failed (${r.failed.join(', ')}).`
                : `Reminders failed (${r.failed.join(', ')}).`,
          })
        )
        .catch((e) => {
          void logAnalyticsEvent('ta_board_feedback_nudge_failed');
          showToast({
            status: 'error',
            description:
              e instanceof Error && e.message ? e.message : 'Could not send the reminder.',
          });
        });
    };
